import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Clock, Wind, Timer, TrendingUp, Award } from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SessionHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      toast.error('Debes iniciar sesión para ver tu historial');
      navigate('/');
      return;
    }
    fetchSessions();
  }, [user]);

  const fetchSessions = async () => {
    try {
      const headers = {};
      const authHeaders = getAuthHeaders();
      if (authHeaders.Authorization) {
        headers.Authorization = authHeaders.Authorization;
      }
      
      const response = await axios.get(`${API}/sessions`, { headers });
      // Limit to last 100 sessions
      const limitedSessions = response.data.slice(0, 100);
      setSessions(limitedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha no disponible';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate average apnea performance
  const getPerformanceData = () => {
    const last10Sessions = sessions.slice(0, 10).reverse();
    return last10Sessions.map((session, index) => {
      const avgApnea = session.apnea_records && session.apnea_records.length > 0
        ? session.apnea_records.reduce((sum, record) => sum + record.duration, 0) / session.apnea_records.length
        : 0;
      
      const sessionDate = session.date || session.completed_at;
      const dateObj = new Date(sessionDate);
      const dateLabel = isNaN(dateObj.getTime()) ? 'N/A' : dateObj.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
      
      return {
        session: `S${index + 1}`,
        apnea: Math.round(avgApnea),
        date: dateLabel
      };
    });
  };

  // Calculate months of practice for medals
  const getMonthsOfPractice = () => {
    if (sessions.length === 0) return 0;
    
    const lastSession = sessions[sessions.length - 1];
    const firstSessionDate = lastSession.date || lastSession.completed_at;
    const firstSession = new Date(firstSessionDate);
    
    if (isNaN(firstSession.getTime())) return 0;
    
    const now = new Date();
    const monthsDiff = (now.getFullYear() - firstSession.getFullYear()) * 12 + (now.getMonth() - firstSession.getMonth());
    return Math.min(monthsDiff + 1, 20); // Max 20 medals
  };

  const performanceData = getPerformanceData();
  const monthsCompleted = getMonthsOfPractice();

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8" data-testid="session-history">
      <div className="mb-6">
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="rounded-full"
          data-testid="back-button"
        >
          <ArrowLeft size={18} className="mr-2" />
          Volver
        </Button>
      </div>

      <h1 className="text-3xl sm:text-4xl font-bold mb-6 sm:mb-8 text-center">Historial de Sesiones</h1>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#77C9D4]"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Performance Chart */}
          {performanceData.length > 0 && (
            <div className="glass rounded-xl p-6">
              <div className="flex items-center mb-4">
                <TrendingUp className="mr-2 text-[#77C9D4]" size={24} />
                <h2 className="text-xl font-semibold">Rendimiento Promedio de Apnea</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">Últimas 10 sesiones</p>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={performanceData}>
                  <defs>
                    <linearGradient id="colorApnea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#77C9D4" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#77C9D4" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#666"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#666"
                    style={{ fontSize: '12px' }}
                    label={{ value: 'Segundos', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #77C9D4',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#2C3E50', fontWeight: 'bold' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="apnea" 
                    stroke="#77C9D4" 
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorApnea)"
                    animationDuration={1500}
                    animationEasing="ease-in-out"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Medals - Months of Practice */}
          <div className="glass rounded-xl p-6">
            <div className="flex items-center mb-4">
              <Award className="mr-2 text-[#FFD700]" size={24} />
              <h2 className="text-xl font-semibold">Progreso Mensual</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Has completado {monthsCompleted} {monthsCompleted === 1 ? 'mes' : 'meses'} de práctica
            </p>
            <div className="grid grid-cols-10 gap-3">
              {Array.from({ length: 20 }, (_, i) => {
                const monthNumber = i + 1;
                const isEarned = monthNumber <= monthsCompleted;
                return (
                  <div key={i} className="flex flex-col items-center">
                    <div 
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center
                        transition-all duration-500 transform
                        ${isEarned 
                          ? 'bg-gradient-to-br from-[#FFD700] to-[#FFA500] shadow-lg scale-110' 
                          : 'bg-gray-200 scale-100 opacity-40'
                        }
                      `}
                      style={{
                        animation: isEarned ? 'pulse 2s infinite' : 'none'
                      }}
                    >
                      <Award 
                        size={20} 
                        className={isEarned ? 'text-white' : 'text-gray-400'}
                      />
                    </div>
                    <span className="text-xs mt-1 text-gray-500">
                      M{monthNumber}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Session List */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Sesiones Recientes</h2>
            {sessions.length === 0 ? (
              <div className="text-center py-12 glass rounded-xl">
                <Wind size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">No has completado ninguna sesión aún</p>
                <Button 
                  onClick={() => navigate('/')}
                  className="mt-4 bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black rounded-full"
                >
                  Comenzar Primera Sesión
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {sessions.map((session, index) => (
                  <div key={index} className="glass rounded-xl p-4 hover:shadow-lg transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="mb-3 sm:mb-0">
                        <h3 className="font-semibold text-lg">{session.pattern_name}</h3>
                        <div className="flex items-center text-sm text-gray-600 mt-1">
                          <Calendar size={14} className="mr-1" />
                          <span>{formatDate(session.date || session.completed_at)}</span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="flex items-center justify-center text-[#77C9D4] mb-1">
                            <Wind size={16} />
                          </div>
                          <p className="text-xs text-gray-600">Rondas</p>
                          <p className="font-semibold">{session.completed_rounds}</p>
                        </div>
                        
                        <div>
                          <div className="flex items-center justify-center text-[#77C9D4] mb-1">
                            <Clock size={16} />
                          </div>
                          <p className="text-xs text-gray-600">Duración</p>
                          <p className="font-semibold">{formatDuration(session.total_time)}</p>
                        </div>
                        
                        {session.apnea_records && session.apnea_records.length > 0 && (
                          <div>
                            <div className="flex items-center justify-center text-[#77C9D4] mb-1">
                              <Timer size={16} />
                            </div>
                            <p className="text-xs text-gray-600">Apnea Prom.</p>
                            <p className="font-semibold">
                              {Math.round(
                                session.apnea_records.reduce((sum, r) => sum + r.duration, 0) / 
                                session.apnea_records.length
                              )}s
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {session.apnea_records && session.apnea_records.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">Tiempos de Apnea por Ronda:</p>
                        <div className="flex flex-wrap gap-2">
                          {session.apnea_records.map((record, i) => (
                            <span 
                              key={i} 
                              className="px-3 py-1 bg-[#77C9D4] bg-opacity-10 text-[#2C3E50] rounded-full text-sm font-medium"
                            >
                              R{record.round_number}: {record.duration}s
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1.1);
          }
          50% {
            transform: scale(1.2);
          }
        }
      `}</style>
    </div>
  );
};

export default SessionHistory;
